import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Search, X, BarChart3 } from 'lucide-react';
import ToolCard from '../components/ToolCard';
import TaxonomyFilter from '../components/TaxonomyFilter';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import config from '../config';

const BACKEND_URL = config.BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BrowsePage = () => {
  const [tools, setTools] = useState([]);
  const [filteredTools, setFilteredTools] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedSubtags, setSelectedSubtags] = useState([]);
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [analytics, setAnalytics] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    filterTools();
  }, [tools, searchTerm, selectedCategories, selectedSubtags]);

  const loadData = async () => {
    try {
      const [toolsRes, analyticsRes] = await Promise.all([
        axios.get(`${API}/tools`),
        axios.get(`${API}/analytics`)
      ]);
      
      setTools(toolsRes.data);
      setFilteredTools(toolsRes.data);
      setAnalytics(analyticsRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterTools = () => {
    let filtered = [...tools];

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(tool =>
        tool.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        tool.description.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Hierarchical taxonomy filtering
    if (selectedCategories.length > 0) {
      filtered = filtered.filter(tool => {
        const toolCategories = tool.categories?.map(c => c.category) || [];
        return selectedCategories.some(cat => toolCategories.includes(cat));
      });
    }

    if (selectedSubtags.length > 0) {
      filtered = filtered.filter(tool => {
        const toolSubtags = tool.categories?.flatMap(c => 
          c.subtags?.map(st => st.toLowerCase()) || []
        ) || [];
        return selectedSubtags.some(subtag => toolSubtags.includes(subtag));
      });
    }

    setFilteredTools(filtered);
  };

  const handleCategoryToggle = (category) => {
    if (selectedCategories.includes(category)) {
      setSelectedCategories(selectedCategories.filter(c => c !== category));
    } else {
      setSelectedCategories([...selectedCategories, category]);
    }
  };

  const handleSubtagToggle = (subtag) => {
    if (selectedSubtags.includes(subtag)) {
      setSelectedSubtags(selectedSubtags.filter(s => s !== subtag));
    } else {
      setSelectedSubtags([...selectedSubtags, subtag]);
    }
  };

  const clearAllFilters = () => {
    setSearchTerm('');
    setSelectedCategories([]);
    setSelectedSubtags([]);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-400">Loading tools...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen px-6 py-12">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl sm:text-5xl font-bold mb-4">
                <span className="gradient-text">Explore Tools</span>
              </h1>
              <p className="text-lg text-gray-400">
                Navigate through {tools.length} curated DevOps and Cloud tools with intelligent taxonomy filtering
              </p>
            </div>
            
            {/* Analytics Toggle */}
            <button
              onClick={() => setShowAnalytics(!showAnalytics)}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-blue-500/20 to-purple-500/20 hover:from-blue-500/30 hover:to-purple-500/30 text-blue-300 border border-blue-500/30 transition-all hover:scale-105"
            >
              <BarChart3 className="w-5 h-5" />
              {showAnalytics ? 'Hide' : 'Show'} Analytics
            </button>
          </div>
        </div>

        {/* Analytics Panel */}
        {showAnalytics && analytics && (
          <div className="mb-8 bg-gradient-to-br from-blue-500/10 to-purple-500/10 backdrop-blur-sm rounded-2xl border border-blue-500/30 p-6">
            <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
              <BarChart3 className="w-6 h-6 text-blue-400" />
              Taxonomy Analytics
            </h2>
            
            <div className="grid md:grid-cols-4 gap-4 mb-6">
              <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-700/50">
                <div className="text-3xl font-bold text-blue-400">{analytics.total_tools}</div>
                <div className="text-sm text-gray-400">Total Tools</div>
              </div>
              
              <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-700/50">
                <div className="text-3xl font-bold text-purple-400">{analytics.active_categories}</div>
                <div className="text-sm text-gray-400">Active Categories</div>
              </div>
              
              <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-700/50">
                <div className="text-3xl font-bold text-green-400">
                  {analytics.taxonomy_coverage?.percentage || 0}%
                </div>
                <div className="text-sm text-gray-400">Taxonomy Coverage</div>
              </div>
              
              <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-700/50">
                <div className="text-3xl font-bold text-yellow-400">
                  {analytics.avg_categories_per_tool}
                </div>
                <div className="text-sm text-gray-400">Avg Categories/Tool</div>
              </div>
            </div>

            {/* Top Categories */}
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-lg font-semibold mb-3 text-gray-200">Top Categories</h3>
                <div className="space-y-2">
                  {Object.entries(analytics.category_distribution || {})
                    .sort(([, a], [, b]) => b - a)
                    .slice(0, 8)
                    .map(([category, count]) => (
                      <div key={category} className="flex items-center justify-between bg-gray-800/30 rounded-lg p-3">
                        <span className="text-gray-300">{category}</span>
                        <div className="flex items-center gap-2">
                          <div className="w-32 h-2 bg-gray-700 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-gradient-to-r from-blue-500 to-purple-500"
                              style={{ width: `${(count / analytics.total_tools) * 100}%` }}
                            />
                          </div>
                          <span className="text-sm font-medium text-blue-400 w-8">{count}</span>
                        </div>
                      </div>
                    ))}
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold mb-3 text-gray-200">Top Subtags</h3>
                <div className="flex flex-wrap gap-2">
                  {analytics.top_subtags?.slice(0, 20).map((item) => (
                    <Badge
                      key={item.subtag}
                      className="bg-purple-500/20 text-purple-300 hover:bg-purple-500/30 border-purple-500/30"
                    >
                      {item.subtag} ({item.count})
                    </Badge>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="grid lg:grid-cols-4 gap-6">
          {/* Left Sidebar - Taxonomy Filter */}
          <div className="lg:col-span-1">
            <div className="sticky top-6">
              {/* Search */}
              <div className="mb-4">
                <label className="block text-sm text-gray-400 mb-2">Search</label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    type="text"
                    placeholder="Search tools..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 bg-gray-900/50 border-gray-600 text-white placeholder-gray-500"
                  />
                </div>
              </div>

              {/* Taxonomy Filter */}
              <TaxonomyFilter
                selectedCategories={selectedCategories}
                selectedSubtags={selectedSubtags}
                onCategoryToggle={handleCategoryToggle}
                onSubtagToggle={handleSubtagToggle}
                onClearAll={clearAllFilters}
              />
            </div>
          </div>

          {/* Right Content - Tools Grid */}
          <div className="lg:col-span-3">
            {/* Results Count */}
            <div className="mb-6">
              <p className="text-gray-400">
                Showing <span className="text-white font-semibold">{filteredTools.length}</span> of{' '}
                <span className="text-white font-semibold">{tools.length}</span> tools
              </p>
            </div>

            {/* Tools Grid */}
            {filteredTools.length > 0 ? (
              <div className="grid md:grid-cols-2 gap-6">
                {filteredTools.map((tool, index) => (
                  <div key={tool.id} className="slide-in" style={{ animationDelay: `${index * 0.05}s` }}>
                    <ToolCard tool={tool} onDelete={loadData} showActions />
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-20 bg-gray-800/20 rounded-2xl border border-gray-700/30">
                <Search className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                <h3 className="text-2xl font-semibold text-gray-400 mb-2">No tools found</h3>
                <p className="text-gray-500 mb-4">Try adjusting your filters or search terms</p>
                {(selectedCategories.length > 0 || selectedSubtags.length > 0 || searchTerm) && (
                  <button
                    onClick={clearAllFilters}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-500/20 hover:bg-blue-500/30 text-blue-300 transition-colors"
                  >
                    <X className="w-4 h-4" />
                    Clear All Filters
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BrowsePage;